import Vue from "vue";
import Vuex from "vuex";
import http from "../axios";
import router from "../router";
import _ from "lodash";
import i18n from "../i18n";

import globalState from "./global/global.state";
import globalMutations from "./global/global.mutations";
import globalActions from "./global/global.actions";
import globalGetters from "./global/global.getters";
import { Mutations } from "./global/global.types";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    authorization: {
      access_token: localStorage.getItem("token")
        ? localStorage.getItem("token")
        : null,
      expires_in: null
    },
    messages: [],
    userTasks: [],
    userActions: [],
    brandLogoUrl: "",
    userPoints: 0,
    userCoins: 0,
    userLevel: 0,
    spinnerVisible: false,
    userRemoveRequestedAt: null,
    unavailableUserState: false,
    pendingConfirmation: null,
    missingProfileFields: [],
    notificationList: [],
    nextLevel: { points: 0 },
    missions: {},
    mgms: 0,
    suggestedRewards: [],
    classification: [],
    ...globalState
  },
  actions: {
    ...globalActions,
    logout({ context }) {
      http
        .get("/api/v1/campaigns/" + this.state.campaign.id + "/users/logout")
        .finally(response => {
          localStorage.removeItem("token");
          this.state.authorization.access_token = null;
          delete http.defaults.headers.common["Authorization"];
          router.push("/");
          this.commit("hideSpinner");
          this.commit(Mutations.SET_USER_PROFILE, {});
          this.commit("updateUserRemoveRequestedAt", null);
          this.commit("updateUserTasks", []);
        });
    },
    async getUserInfo({ context }) {
      if (this.state.campaign !== undefined) {
        let response = await http.get(
          "/api/v1/campaigns/" + this.state.campaign.id + "/users/info"
        );
        let user = _.get(response, "data.data.user");
        this.commit(Mutations.SET_USER_PROFILE, user);
        this.state.unavailableUserState = false;
        this.commit("updateUserPoints", _.get(user, "points", user.points));
        this.commit("updateUserCoins", _.get(user, "coins", user.coins));
        this.commit("updateUserLevel", _.get(user, "level", user.level));
        this.commit(
          "updateUserRemoveRequestedAt",
          _.get(user, "remove_requested_at", null)
        );
      }
    },
    async getUserTasks({ context }) {
      let response = await http.get(
        "/api/v1/campaigns/" + this.state.campaign.id + "/users/tasks"
      );
      this.commit("updateUserTasks", _.get(response, "data.data.tasks", []));
    },
    async getUnreadMessages({ context }) {
      let response = await http.get(
        "/api/v1/campaigns/" + this.state.campaign.id + "/users/messages/unread"
      );
      this.commit("updateMessages", _.get(response, "data.data.messages", []));

      _.forEach(this.state.messages, message => {
        let messagePoints = _.get(message, "points", "");
        if (messagePoints.length > 0) {
          this.dispatch("getUserInfo");
          return false;
        }
      });
    },
    async getUserActions({ context }) {
      let response = await http.get(
        "/api/v1/campaigns/" + this.state.campaign.id + "/users/tasks/done"
      );
      this.commit("updateUserActions", _.get(response, "data.data.done", []));
    },
    async fetchNotifications({ commit }) {
      let notifications = [];

      const friendsResponse = await http.get(
        `/api/v1/campaigns/${
          this.state.campaign.id
        }/leaderboard/friends/passedby`
      );
      const { friend } = friendsResponse.data.data;
      if (friend) {
        const friendNotify = {
          ...friend,
          text: `${friend.first_name} ${
            friend.last_name
          } has passed you in the standings`,
          type: "leaderboard-friend",
          id: friend.userId
        };
        notifications = [friendNotify];
      }

      const boxesResponse = await http.get(
        `/api/v1/campaigns/${
          this.state.campaign.id
        }/dashboard/information/boxes`
      );
      if (boxesResponse.data.data.length) {
        notifications = notifications.concat(boxesResponse.data.data);
      }

      // const coinResponse = await http.get(
      //   `/api/v1/campaigns/${this.state.campaign.id}/catalog/rewards/coins/next`
      // );
      // if (coinResponse.data.data) {
      //   notifications.push(coinResponse.data.data);
      // }

      commit("updateNotifications", notifications);
    },
    async fetchUserPoints({ commit }) {
      const response = await http.get(
        "/api/v1/campaigns/" + this.state.campaign.id + "/users/levels/next"
      );
      if (response.data.data) {
        commit("updateNextLevel", response.data.data);
      } else {
        commit("updateNextLevel", { noMoreLevels: true });
      }
    },
    async fetchMissionsStatistics({ commit }) {
      const response = await http.get(
        "/api/v1/campaigns/" +
          this.state.campaign.id +
          "/users/missions/completed"
      );
      commit("updateMissions", response.data.data);
    },
    async fetchUserMgms({ commit }) {
      const response = await http.get(
        "/api/v1/campaigns/" +
          this.state.campaign.id +
          "/users/mgms/pending/count"
      );
      commit("updateMgms", response.data.data);
    },
    async fetchSuggestedCoinsReward({ commit }) {
      const response = await http.get(
        "/api/v1/campaigns/" +
          this.state.campaign.id +
          "/catalog/rewards/coins/suggested"
      );
      if (response.data.data && response.data.data.suggested_rewards.length) {
        commit("updateSuggestedRewards", response.data.data.suggested_rewards);
      }
    },
    async fetchLeaderboard({ commit }) {
      const response = await http.get(
        "/api/v1/campaigns/" + this.state.campaign.id + "/leaderboard"
      );
      if (response.data.data.length) {
        commit("updateClassification", response.data.data);
      }
    },
    setView({ commit }, view) {
      commit("updateView", view);
    }
  },
  mutations: {
    ...globalMutations,
    updateClassification(state, classification) {
      state.classification = classification;
    },
    updateSuggestedRewards(state, rewards) {
      state.suggestedRewards = rewards;
    },
    updateMgms(state, mgms) {
      state.mgms = mgms;
    },
    updateMissions(state, missions) {
      state.missions = missions;
    },
    unshiftMessage(state, message) {
      state.messages.unshift(message);
    },
    pushMessage(state, message) {
      state.messages.push(message);
    },
    dismissMessage(state) {
      state.messages.pop();
    },
    updateUser(state, userInfo) {
      state.user = userInfo;
    },
    updateUserPoints(state, points) {
      state.userPoints = points;
    },
    updateUserCoins(state, coins) {
      state.userCoins = coins;
    },
    updateUserLevel(state, level) {
      if (
        state.userLevel !== undefined &&
        state.userLevel !== 0 &&
        level !== state.userLevel
      ) {
        this.commit("unshiftMessage", {
          type: "success",
          title: i18n.t("Congratulazioni!"),
          text: i18n.t("Hai raggiunto {nome_livello}", {
            nome_livello: level
          })
        });
      }
      state.userLevel = level;
    },
    updateUserTasks(state, tasks) {
      state.userTasks = tasks;
    },
    updateUserActions(state, actions) {
      state.userActions = actions;
    },
    updateMessages(state, messages) {
      state.messages = messages;
    },
    updateBrand(state, brand) {},
    updateBrandLogo(state, brandLogoUrl) {
      state.brandLogoUrl = brandLogoUrl;
    },
    showSpinner(state) {
      state.spinnerVisible = true;
    },
    hideSpinner(state) {
      state.spinnerVisible = false;
    },
    updateUserRemoveRequestedAt(state, requestedAt) {
      state.userRemoveRequestedAt = requestedAt;
    },
    updateMissingProfileFields(state, missingFields) {
      state.missingProfileFields = missingFields;
    },
    updatePendingConfirmation(state, pendingConfirmation) {
      state.pendingConfirmation = pendingConfirmation;
    },
    updateNotifications(state, notifications) {
      state.notificationList = notifications;
    },
    updateNextLevel(state, nextLevel) {
      state.nextLevel = nextLevel;
    }
  },
  getters: {
    ...globalGetters,
    isLoggedIn: state => !!state.authorization.access_token,
    getUserPoints: state => state.userPoints,
    getUserCoins: state => state.userCoins,
    getUserLevel: state => state.userLevel,
    getUserTasks: state =>
      state.userTasks.filter(task => task.type !== "upload"),
    getUserActions: state => state.userActions,
    getBrandLogo: state => _.get(state, "brand.logo", ""),
    isSpinnervisible: state => state.spinnerVisible,
    getUserRemoveRequestedAt: state => state.userRemoveRequestedAt,
    getMissingProfileFields: state => state.missingProfileFields,
    getPendingConfirmation: state => state.pendingConfirmation,
    campaign: state => state.campaign,
    getNotifications: state => state.notificationList,
    getUser: state => state.user,
    nextLevel: state => state.nextLevel,
    getMissionsStatistics: state => state.missions,
    getMgms: state => state.mgms,
    getSuggestedRewards: state => state.suggestedRewards,
    getClassification: state => state.classification
  }
});
